import React, { useState } from "react"
import { Fuel } from "lucide-react"
import MapManager from "@/components/maps/MapManager"
import { parseLatLng } from "@/components/maps/coordinateParser"
import { useReverseGeocoder } from "@/hooks/useReverseGeocode"
import { useTranslation } from "react-i18next"

export type AddFuelStationMenuProps = {
  mapManagerInstance: MapManager | null
  /** Shared with the parent context menu so click-outside detection still works. */
  menuRef: React.RefObject<HTMLDivElement>
  position: { x: number; y: number }
  /** Return to the main context menu. */
  onBack: () => void
  /** Close the whole context menu. */
  onClose: () => void
}

/**
 * Submenu for adding a fuel station. Accepts raw coordinates or a Google Maps
 * link, reverse-geocodes the position, and inserts it as a `refuel` stop at the
 * start of the route (the first stop right after the current driver location).
 */
const AddFuelStationMenu: React.FC<AddFuelStationMenuProps> = ({
  mapManagerInstance,
  menuRef,
  position,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation()
  const [input, setInput] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const reverseGeocode = useReverseGeocoder()

  const handleAddFuelStation = async () => {
    const coords = parseLatLng(input)
    if (!coords) {
      setError(t("maps.addFuelStationMenu.invalidInput"))
      return
    }
    if (!mapManagerInstance) return

    setError(null)
    setSubmitting(true)

    const [lng, lat] = coords
    let formattedAddress: string | undefined
    try {
      const result = await reverseGeocode(lat, lng)
      formattedAddress = result.formattedAddress ?? undefined
    } catch {
      // Fall back to raw coordinates as the address
      formattedAddress = `${lat},${lng}`
    }

    try {
      // afterStopIndex = -1 -> first stop, right after current driver location
      await mapManagerInstance.addStopAfterIndex(
        [lng, lat],
        -1,
        "refuel",
        formattedAddress ? { formattedAddress } : undefined,
      )
    } finally {
      setSubmitting(false)
      onClose()
    }
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-[1000] min-w-[300px] max-w-lg overflow-hidden rounded-md bg-popover text-sm text-popover-foreground shadow-md"
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <div className="p-3">
        <div className="flex items-start gap-3 text-xs font-normal">
          <div className="mt-1 flex-shrink-0">
            <Fuel className="h-4 w-4" />
          </div>
          <div className="flex-1 space-y-1">
            <div className="font-medium">
              {t("maps.addFuelStationMenu.title")}
            </div>
            <div className="text-muted-foreground">
              {t("maps.addFuelStationMenu.description")}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col border-t p-1">
        <button
          className="flex items-center gap-3 rounded-sm px-3 py-2 text-xs hover:bg-accent hover:text-accent-foreground"
          onClick={onBack}
        >
          <svg
            className="h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t("common.back")}
        </button>

        <div className="space-y-2 p-2">
          <input
            type="text"
            autoFocus
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
              if (error) setError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !submitting) {
                e.preventDefault()
                handleAddFuelStation()
              }
            }}
            placeholder={t("maps.addFuelStationMenu.placeholder")}
            className="w-full rounded-sm border bg-background px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-ring"
          />
          {error ? (
            <div className="text-xs text-destructive">{error}</div>
          ) : null}
          <button
            className="flex w-full items-center justify-center gap-2 rounded-sm bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            onClick={handleAddFuelStation}
            disabled={submitting || !input.trim()}
          >
            <Fuel className="h-4 w-4" />
            {submitting
              ? t("maps.addFuelStationMenu.adding")
              : t("maps.addFuelStationMenu.submit")}
          </button>
        </div>
      </div>
    </div>
  )
}

export default AddFuelStationMenu
